import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '../../app.config';

export interface AdditionalInsuredValidation {
    phoneValid: boolean | null;
    phoneCellular: boolean | null;
    emailValid: boolean | null;
}

export interface AdditionalInterestValidation {
    emailValid: boolean | null;
}

export interface EscrowValidation {
    emailValid: boolean | null;
}

export interface DesignatedThirdPartyValidation {
    emailValid: boolean | null;
}

export interface CoOwnerValidation {
    phoneValid: boolean | null;
    phoneCellular: boolean | null;
    emailValid: boolean | null;
}

export interface ContactValidationResult {
    insuredPhoneValid: boolean | null;
    insuredPhoneCellular: boolean | null;
    insuredEmailValid: boolean | null;
    coOwnerValidation: CoOwnerValidation[];
    producerPhoneValid: boolean | null;
    producerPhoneCellular: boolean | null;
    producerEmailValid: boolean | null;
    additionalInsuredValidation: AdditionalInsuredValidation[];
    additionalInterestValidation: AdditionalInterestValidation[];
    designatedThirdPartyValidation: DesignatedThirdPartyValidation[];
    escrowValidation: EscrowValidation[];
}

/** Normalises the isvalid / issmscapable string to boolean.
 *  Handles both "true" (verifyPhoneNumber / zerobounce) and
 *  "True" (elastic) that the backend can return. */
function isTrue(value: string | undefined | null): boolean {
    return value?.toLowerCase() === 'true';
}

@Injectable({ providedIn: 'root' })
export class VerifyContactService {
    constructor(private http: HttpClient, @Inject(APP_CONFIG) private config: AppConfig) {}

    verifySingleContact(value: string, type: 'Phone' | 'Email'): Observable<{ isValid: boolean; isCellular: boolean }> {
        const request = type === 'Phone'
            ? { phone: value, persona: 'Insured', contacttype: 'Phone', validateDuplicateContactAgainstPersona: 'Producer' }
            : { email: value, persona: 'Insured', contacttype: 'Email', validateDuplicateContactAgainstPersona: 'Producer' };
        return this.http
            .post<{ statusCode: number; body: { validateContactResponse: any[] } }>(
                this.config.verifyContactUrl,
                { validateContactRequest: [request] },
                { headers: { 'x-api-key': this.config.verifyContactApiKey } }
            )
            .pipe(
                map((res) => {
                    const r = res?.body?.validateContactResponse?.[0];
                    return { isValid: isTrue(r?.isvalid), isCellular: isTrue(r?.issmscapable) };
                }),
                catchError(() => of({ isValid: false, isCellular: false }))
            );
    }

    verifyContacts(
        insuredPhone: string,
        insuredEmail: string,
        coOwners: { phone: string; email: string }[] = [],
        producerPhone: string = '',
        producerEmail: string = '',
        additionalInsureds: { phone: string; email: string }[] = [],
        additionalInterests: { email: string }[] = [],
        designatedThirdParties: { email: string }[] = [],
        escrows: { email: string }[] = []
    ): Observable<ContactValidationResult> {
        const requests: any[] = [];
        if (insuredPhone) requests.push({ phone: insuredPhone, persona: 'Insured', contacttype: 'Phone' });
        if (insuredEmail) requests.push({ email: insuredEmail, persona: 'Insured', contacttype: 'Email' });
        coOwners.forEach(({ phone, email }) => {
            if (phone) requests.push({ phone, persona: 'Insured', contacttype: 'Phone' });
            if (email) requests.push({ email, persona: 'Insured', contacttype: 'Email' });
        });
        if (producerPhone) requests.push({ phone: producerPhone, persona: 'Producer', contacttype: 'Phone' });
        if (producerEmail) requests.push({ email: producerEmail, persona: 'Producer', contacttype: 'Email' });
        additionalInsureds.forEach(({ phone, email }) => {
            if (phone) requests.push({ phone, persona: 'AdditionalInsured', contacttype: 'Phone' });
            if (email) requests.push({ email, persona: 'AdditionalInsured', contacttype: 'Email' });
        });
        additionalInterests.forEach(({ email }) => {
            if (email) requests.push({ email, persona: 'AdditionalInterest', contacttype: 'Email' });
        });
        designatedThirdParties.forEach(({ email }) => {
            if (email) requests.push({ email, persona: 'AdditionalInterest', contacttype: 'Email' });
        });
        escrows.forEach(({ email }) => {
            if (email) requests.push({ email, persona: 'AdditionalInterest', contacttype: 'Email' });
        });

        const empty: ContactValidationResult = { insuredPhoneValid: null, insuredPhoneCellular: null, insuredEmailValid: null, producerPhoneValid: null, producerPhoneCellular: null, producerEmailValid: null, additionalInsuredValidation: [], additionalInterestValidation: [], designatedThirdPartyValidation: [], escrowValidation: [], coOwnerValidation: [] };
        if (!requests.length) return of(empty);

        return this.http
            .post<{ statusCode: number; body: { validateContactResponse: any[] } }>(
                this.config.verifyContactUrl,
                { validateContactRequest: requests },
                { headers: { 'x-api-key': this.config.verifyContactApiKey } }
            )
            .pipe(
                map((res) => {
                    // Response order mirrors request order exactly — consume positionally with a cursor.
                    const responses = res?.body?.validateContactResponse ?? [];
                    let idx = 0;

                    const phoneRes       = insuredPhone  ? responses[idx++] : null;
                    const emailRes       = insuredEmail  ? responses[idx++] : null;

                    const coOwnerValidation: CoOwnerValidation[] = coOwners.map(({ phone, email }) => {
                        const coPhone = phone ? responses[idx++] : null;
                        const coEmail = email ? responses[idx++] : null;
                        return {
                            phoneValid:    phone ? isTrue(coPhone?.isvalid)    : null,
                            phoneCellular: phone ? isTrue(coPhone?.issmscapable) : null,
                            emailValid:    email ? isTrue(coEmail?.isvalid)    : null,
                        };
                    });

                    const prodPhoneRes   = producerPhone ? responses[idx++] : null;
                    const prodEmailRes   = producerEmail ? responses[idx++] : null;

                    const additionalInsuredValidation: AdditionalInsuredValidation[] = additionalInsureds.map(({ phone, email }) => {
                        const aiPhone = phone ? responses[idx++] : null;
                        const aiEmail = email ? responses[idx++] : null;
                        return {
                            phoneValid:    phone ? isTrue(aiPhone?.isvalid)      : null,
                            phoneCellular: phone ? isTrue(aiPhone?.issmscapable) : null,
                            emailValid:    email ? isTrue(aiEmail?.isvalid)      : null,
                        };
                    });

                    const additionalInterestValidation: AdditionalInterestValidation[] = additionalInterests.map(({ email }) => {
                        const atEmail = email ? responses[idx++] : null;
                        return {
                            emailValid: email ? isTrue(atEmail?.isvalid) : null,
                        };
                    });

                    const designatedThirdPartyValidation: DesignatedThirdPartyValidation[] = designatedThirdParties.map(({ email }) => {
                        const dtpEmail = email ? responses[idx++] : null;
                        return {
                            emailValid: email ? isTrue(dtpEmail?.isvalid) : null,
                        };
                    });

                    const escrowValidation: EscrowValidation[] = escrows.map(({ email }) => {
                        const escEmail = email ? responses[idx++] : null;
                        return {
                            emailValid: email ? isTrue(escEmail?.isvalid) : null,
                        };
                    });

                    return {
                        insuredPhoneValid:    insuredPhone ? isTrue(phoneRes?.isvalid)      : null,
                        insuredPhoneCellular: insuredPhone ? isTrue(phoneRes?.issmscapable) : null,
                        insuredEmailValid:    insuredEmail ? isTrue(emailRes?.isvalid)      : null,
                        coOwnerValidation,
                        producerPhoneValid:    producerPhone ? isTrue(prodPhoneRes?.isvalid)      : null,
                        producerPhoneCellular: producerPhone ? isTrue(prodPhoneRes?.issmscapable) : null,
                        producerEmailValid:    producerEmail ? isTrue(prodEmailRes?.isvalid)      : null,
                        additionalInsuredValidation,
                        additionalInterestValidation,
                        designatedThirdPartyValidation,
                        escrowValidation,
                    };
                }),
                catchError(() => of(empty))
            );
    }
}
